import { useParams } from 'react-router-dom';
import useDelete from '~/lib/useDelete';
import useBusinessNavigate from '~/lib/useBusinessNavigate';
import React, { useState } from 'react';
import * as Toast from '~/lib/Toast';
import { setEventTypesQuery, setPackagesQuery } from '~/lib/Queries';
import PageHeading from '~/components/PageHeading';
import DotDotDotMenu from '~/components/DotDotDotMenu';
import SectionPicker from '~/components/SectionPicker';
import PackageEditForm from '~/components/packages/PackageEditForm';
import FlowBuilder from '~/components/flows/FlowBuilder';
import GrantList, {
    GrantColumnHeadings,
    GrantListRow,
} from '~/components/packages/GrantList';
import { ucfirst } from '~/lib/Utils';
import CopyIcon from '~/components/icons/CopyIcon';
import useBusinessPostApi from '~/lib/useBusinessPostApi';

const PackageEditPageTemplate = ({
    singular,
    plural,
    basePath,
    type,
    initialTab,
}) => {
    const { packageId } = useParams();
    const grantListRef = React.useRef(null);
    const { saveDelete, isDeleting } = useDelete(
        `/packages/${packageId}/delete`,
        {
            prompt: `Are you sure you want to permanently delete this ${singular}?`,
        },
    );
    const { save: saveDuplicate, isSaving: isDuplicating } = useBusinessPostApi(
        `/packages/${packageId}/duplicate`,
    );

    const navigate = useBusinessNavigate();
    const [section, setSection] = useState(initialTab || 'edit');
    const [name, setName] = useState('');

    const isNew = packageId === '0';

    function doDelete() {
        saveDelete().then(result => {
            Toast.success(`Successfully deleted ${singular}`);
            navigate(basePath);
            setPackagesQuery(result, type);
        });
    }

    function doDuplicate() {
        saveDuplicate({}).then(result => {
            setPackagesQuery(result.allPackages, type);
            navigate(`${basePath}/${result.newPackage.id}`);
            Toast.success(`Successfully duplicated ${singular}`);
        });

        return true;
    }

    return (
        <div>
            <PageHeading
                heading={name || `New ${ucfirst(singular)}`}
                crumbs={[
                    {
                        name: ucfirst(plural),
                        href: basePath,
                    },
                ]}
                rightContent={
                    !isNew && (
                        <DotDotDotMenu
                            btnClassName="btn-gray-circle"
                            options={[
                                {
                                    name: isDuplicating
                                        ? 'Duplicating...'
                                        : 'Duplicate',
                                    icon: <CopyIcon />,
                                    onClick: doDuplicate,
                                },
                                {
                                    name: isDeleting ? 'Deleting...' : 'Delete',
                                    onClick: () => doDelete(),
                                    isDelete: true,
                                },
                            ]}
                        />
                    )
                }
            />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between max-w-4xl">
                <SectionPicker
                    className="mb-4"
                    value={section}
                    onChange={setSection}
                    options={[
                        { value: 'edit', label: 'Edit' },
                        { value: 'flows', label: 'Automations' },
                        { value: 'clients', label: 'Clients' },
                    ]}
                />
                {section === 'clients' && (
                    <button
                        type="button"
                        className="btn-light-pill mb-4 float-right"
                        onClick={() => grantListRef.current?.showNewGrant()}
                    >
                        Add {ucfirst(singular)} to Client
                    </button>
                )}
            </div>

            {section === 'edit' && (
                <PackageEditForm
                    key={packageId}
                    packageId={packageId}
                    onChangeName={setName}
                    type={type}
                    singular={singular}
                    basePath={basePath}
                />
            )}
            {section === 'flows' && (
                <>
                    {isNew && <div>Please save this to set up automations</div>}
                    {!isNew && (
                        <FlowBuilder
                            flowableId={packageId}
                            flowableType="package"
                        />
                    )}
                </>
            )}
            {section === 'clients' && (
                <>
                    {isNew && (
                        <div>
                            Please save this to view and assign to clients
                        </div>
                    )}
                    {!isNew && (
                        <div className="max-w-4xl">
                            <GrantList
                                ref={grantListRef}
                                showFilters={true}
                                search={{
                                    packageId: packageId,
                                }}
                                headerContent={() => <GrantColumnHeadings />}
                                rowContent={({ grant }) => (
                                    <GrantListRow grant={grant} />
                                )}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PackageEditPageTemplate;
